/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionDeselectAll';

const ActionDeselectAllManager: ActionManagerFn<'ActionDeselectAll'> = (
  platform: Platform,
): ActionManager<'ActionDeselectAll'> => {
  return {
    ...baseActionManager(platform, 'ActionDeselectAll'),
    defaultValue,
    props: {
      ...baseActionProps,
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'ReadOnlyDataStore',
        ],
      },
    },
    name: 'Deselect All Records',
    replaceableWith: ['ActionSelectAll'],
    getDisplayText: (item) => item.props.alias,
    icon: { iconName: 'square' },
    description: `**Overview**<br/>
The "Deselect All Records" action is a UI state management component designed to reset the selection state of a Data Store. Its primary function is to programmatically clear any active selections (e.g., checked rows in a grid or highlighted items in a list), reverting the data view to a neutral state where no specific records are targeted.<br/><br/>
This component operates directly on the Data Store entity, ensuring that any UI component bound to that store (such as a Data Grid or List View) automatically updates to reflect the deselection. It is designed to be easily interchangeable with the ActionSelectAll component, offering symmetrical control over bulk selection states.

**Key Features**<br/>
* **Global Selection Reset:** Explicitly clears the selection status for all records within the target Data Store, ensuring no artifacts remain selected.
* **Component Interchangeability:** Configured in the replaceableWith array to be directly swappable with ActionSelectAll, simplifying the process of creating toggle buttons (Select All / Deselect All).
* **Broad Store Compatibility:** Supports interaction with CloudDataStore, DataStore, LocalDataStore, and ReadOnlyDataStore, allowing it to manage selection states across persistent, local, and read-only data contexts.

**Properties**<br/>
* **Alias (Required):** A DataSourceAliasChooser property that identifies the specific Data Store to be reset.
  * *Function:* The action targets the internal state of this alias to remove all "selected" flags.

**Use Cases**<br/>
* **Bulk Operation Reset:** A "Cancel Selection" button that appears after a user has selected multiple items for a batch action, allowing them to quickly undo their selection.
* **Workflow Cleanup:** Automatically clearing selections after a batch operation (like "Delete Selected" or "Approve Selected") has successfully completed, preparing the grid for the next task.`,
  };
};

export default ActionDeselectAllManager;
